import type { Core as ApryseCore, WebViewerInstance } from '@pdftron/webviewer';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  jumpToAnnotation,
  removeAnnotations,
  renderReadOnlyAnnotations,
} from 'modules/common/viewer/pdfViewer/data/actions';
import { REDACT_SUBJECT } from 'modules/common/viewer/pdfViewer/data/constants';
import {
  type ReadOnlyAnnotation,
  getAnnotationRemovalQueue,
  getJumpToAnnotationId,
  getReadOnlyAnnotationIds,
  getReadOnlyAnnotationRenderQueue,
} from 'modules/common/viewer/pdfViewer/data/reducer';

import { scrollToAnnotation } from '../annotationCommands/suggestionUtils';

type UseAnnotationInteractionsProps = {
  isLoaded: boolean;
  instance: WebViewerInstance | null;
  selectedAnnotationIds: Set<string>;
  isStandaloneViewer: boolean;
};

/**
 * Responsible for managing the interactions with standard and chat driven annotations
 */
export const useAnnotationInteractions = ({
  isLoaded,
  instance,
  selectedAnnotationIds,
  isStandaloneViewer,
}: UseAnnotationInteractionsProps) => {
  const [isRenderingReadOnlyAnnotations, setIsRenderingReadOnlyAnnotations] =
    useState(false);

  const dispatch = useDispatch();

  // handle focusing on annotations
  // NOTE: used for jumping to read only annotation selected from chat
  const jumpToAnnotationId: string | null = useSelector(getJumpToAnnotationId);
  useEffect(() => {
    if (instance && isLoaded && jumpToAnnotationId) {
      const annotationManager = instance.Core.annotationManager;
      const annotation =
        annotationManager.getAnnotationById(jumpToAnnotationId);

      if (annotation) {
        scrollToAnnotation(annotation, instance.Core.documentViewer);
      } else {
        console.error(
          `Unable to find annotationById when focusing read only annotation: ${jumpToAnnotationId}`,
        );
      }
      dispatch(jumpToAnnotation.success());
    }
  }, [dispatch, instance, isLoaded, jumpToAnnotationId]);

  // handle conditionally displaying the editing button on the annotation popup when selecting annotations
  const readOnlyAnnotationIds = useSelector(getReadOnlyAnnotationIds);
  useEffect(() => {
    if (instance) {
      if (
        selectedAnnotationIds.size === 0 ||
        readOnlyAnnotationIds.some((id) => selectedAnnotationIds.has(id))
      ) {
        instance.UI.disableElements(['annotationStyleEditButton']);
      } else {
        instance.UI.enableElements(['annotationStyleEditButton']);
      }
    }
  }, [instance, readOnlyAnnotationIds, selectedAnnotationIds]);

  // handle removing annotations (related to chat)
  const annotationRemovalQueue = useSelector(getAnnotationRemovalQueue);
  useEffect(() => {
    if (instance && isLoaded && annotationRemovalQueue.length) {
      const annotationManager = instance.Core.annotationManager;
      const annotations: ApryseCore.Annotations.Annotation[] = [];
      annotationRemovalQueue.forEach((annotationId) => {
        const annotation = annotationManager.getAnnotationById(annotationId);
        annotations.push(annotation);
      });
      annotationManager.deleteAnnotations(annotations);
      dispatch(
        removeAnnotations.success({ annotationIds: annotationRemovalQueue }),
      );
    }
  }, [dispatch, isLoaded, annotationRemovalQueue, instance]);

  // handle rendering read-only annotations
  // read only annotations are used for chat
  const readOnlyAnnotationsRenderQueue = useSelector(
    getReadOnlyAnnotationRenderQueue,
  );
  useEffect(() => {
    async function importReadOnlyAnnotations(
      readOnlyAnnotations: ReadOnlyAnnotation[],
    ) {
      if (!instance) return;

      // 1. Render the annotations
      const annotations: ApryseCore.Annotations.Annotation[] =
        await instance.Core.annotationManager.importAnnotations(
          `<annots>${readOnlyAnnotations
            .map(({ annotation }) => annotation)
            .join('')}</annots>`,
        );

      if (!annotations.length) {
        console.warn(
          'Failed to import read only annotations. This means the document the annotation belongs to is likely not properly loaded yet.',
        );
        return;
      }

      // 2. Update the annotations for desired 'read only' behavior
      annotations.forEach((annotation) => {
        annotation.NoMove = true;
        annotation.NoResize = true;
        annotation.disableRotationControl();
      });

      // 3. Jump to the first annotation
      instance.UI.setZoomLevel(readOnlyAnnotations[0]?.zoom || 1);
      scrollToAnnotation(annotations[0], instance.Core.documentViewer);

      // 4. Acknowledge successfully removed annotations
      dispatch(
        renderReadOnlyAnnotations.success({
          annotationIds: annotations.map((annotation) => annotation.Id),
        }),
      );
      // 5. Clear the functions busy flag to handle additional work
      setIsRenderingReadOnlyAnnotations(false);
    }

    if (
      instance &&
      isLoaded &&
      readOnlyAnnotationsRenderQueue.length &&
      !isRenderingReadOnlyAnnotations &&
      !isStandaloneViewer
    ) {
      setIsRenderingReadOnlyAnnotations(true);
      importReadOnlyAnnotations(readOnlyAnnotationsRenderQueue);

      instance.UI.setToolbarGroup('toolbarGroup-Annotate');

      // remove redaction annotations when importing annotations from chat
      instance.Core.annotationManager.deleteAnnotations(
        instance.Core.annotationManager
          .getAnnotationsList()
          .filter(({ Subject }) => Subject === REDACT_SUBJECT),
      );
    }
  }, [
    dispatch,
    instance,
    isLoaded,
    isRenderingReadOnlyAnnotations,
    isStandaloneViewer,
    readOnlyAnnotationsRenderQueue,
    setIsRenderingReadOnlyAnnotations,
  ]);
};
